/**
 * Aruba sites store — drives the /aruba/sites page.
 *
 * Same load-or-fall-back-to-fixture shape as aruba-devices/aruba-groups.
 * Read-only — there's no /aruba/sites/ POST endpoint yet (and no React
 * surface that wants to create sites either; sites are managed in Central).
 */
import { create } from "zustand";
import { get as apiGet, ApiError } from "@/api/client";
import { ArubaSiteListSchema, type ArubaSite } from "@/api/schemas/aruba";

interface State {
  data: ArubaSite[];
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;
  load: () => Promise<void>;
}

export const useArubaSites = create<State>((set, get) => ({
  data: [],
  status: "idle",
  error: null,

  load: async () => {
    if (get().status === "loading") return;
    set({ status: "loading", error: null });
    try {
      const raw = await apiGet<unknown>("/aruba/sites/");
      set({ data: ArubaSiteListSchema.parse(raw), status: "ready" });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      // No fixtures for sites yet — fall back to empty so the page can show
      // an "unable to load" banner with the real cause.
      set({
        data: [],
        status: "error",
        error: message,
      });
    }
  },
}));
